// Case study page: renders a study's content blocks (parsed from the original site) with an M3 heading hierarchy.
//   page title = Display Small (h1) · section = Headline Small (h2) · sub-section = Title Large (h3) · body = Body Large
// Blog layout: plain flowing text; only quotes sit in a highlighted card (accent container).
// Block types: h {l,x} · p {x} · quote {x} · list {icon,items[{x,tags}]} · img {u,c,size,align,valign} · media {u,c,x,side} · divider {size,look} · grid {cols,gap,align,layout,items[] (each may set span)} · card {img,u,c,h,x} · figma {u,link} · video {u,c} · table {head,align[],rows[][],c} · code {code,lang,c} · hero {x,sub,tags}; text blocks take an optional `s` (M3 type style) (see PortfolioData in portfolioData.js).
(function () {
  const { Box, Typography } = MaterialUI;

  // Optional per-block Material 3 type style ("headline-large", "body-small", ...); undefined keeps the block's default.
  function typeSx(style) {
    if (!style) return null;
    const [family, size] = String(style).split('-');
    const t = size && window.Components.m3Typescale[family + size[0].toUpperCase() + size.slice(1)];
    if (!t) return null;
    const px = parseFloat(t.fontSize) * 16;
    // Large display/headline sizes step down on compact screens so they do not overflow.
    return { fontSize: { xs: px >= 32 ? `${Math.round(px * 0.75)}px` : t.fontSize, md: t.fontSize }, lineHeight: t.lineHeight, fontWeight: t.fontWeight, letterSpacing: t.letterSpacing };
  }

  // Automatic heading style from its level, so a heading looks right without picking a style: page title (h1) = display-small,
  // section (h2) = headline-small, sub-section (h3) = title-large. In a narrow place (a cell of a grid with 3+ columns) it steps down one.
  // An explicit style on the block always wins; the editor's Reset removes it.
  const HEADING_STYLE = { 1: 'display-small', 2: 'headline-small', 3: 'title-large' };
  const HEADING_COMPACT = { 'display-small': 'headline-small', 'headline-small': 'title-large', 'title-large': 'title-medium' };
  const headingStyle = (level, explicit, compact) => explicit || (compact ? HEADING_COMPACT[HEADING_STYLE[level]] : HEADING_STYLE[level]);

  const DIVIDER_SPACE = { small: 2, medium: 4, large: 7 };

  // Divider looks: 'line' (hairline), 'wavy' (Material 3 Expressive wave, drawn as a repeating SVG pattern), 'none' (spacing only).
  // `size` sets the spacing around it, and the wave's amplitude and wavelength.
  function Divider({ look = 'line', size = 'medium' }) {
    const my = DIVIDER_SPACE[size] || 4;
    if (look === 'none') return <Box aria-hidden="true" sx={{ my }} />;
    if (look === 'wavy') {
      const k = { small: 0.75, medium: 1, large: 1.5 }[size] || 1;
      const w = 24 * k, h = 12 * k, mid = h / 2;
      const id = React.useId();
      return (
        <Box role="separator" sx={{ my, color: 'var(--m3-primary)', lineHeight: 0 }}>
          <svg width="100%" height={h} aria-hidden="true" style={{ display: 'block', overflow: 'hidden' }}>
            <defs>
              <pattern id={id} width={w} height={h} patternUnits="userSpaceOnUse">
                <path d={`M0 ${mid} Q ${w / 4} ${mid - h * 0.4} ${w / 2} ${mid} T ${w} ${mid}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </pattern>
            </defs>
            <rect width="100%" height={h} fill={`url(#${id})`} />
          </svg>
        </Box>
      );
    }
    return <Box component="hr" sx={{ border: 0, borderTop: '1px solid var(--m3-outline-variant)', my }} />;
  }
  const URL_RE = /(https?:\/\/[^\s]+)/g;

  // Inline styles: **bold**, _italic_ (only at word edges, so snake_case and URLs are safe), ~~strikethrough~~. They nest and combine.
  const STYLES = [
    [/\*\*(\S(?:[\s\S]*?\S)?)\*\*/, 'strong', 0],
    [/~~(\S(?:[\s\S]*?\S)?)~~/, 's', 0],
    [/(^|[^\w])_(\S(?:[\s\S]*?\S)?)_(?!\w)/, 'em', 1]
  ];
  // Splits text on the earliest style marker, rendering the styled part recursively; plain text goes through `leaf`.
  function styled(text, leaf, path = 'k') {
    const out = [];
    let rest = text, n = 0;
    while (rest) {
      let best = null;
      STYLES.forEach(([re, tag, lead]) => {
        const m = re.exec(rest);
        if (m) { const at = m.index + (lead ? m[1].length : 0); if (!best || at < best.at) best = { m, tag, lead, at }; }
      });
      if (!best) { out.push(...leaf(rest, `${path}${n++}`)); break; }
      const { m, tag, lead, at } = best;
      if (at > 0) out.push(...leaf(rest.slice(0, at), `${path}${n++}`));
      const key = `${path}${n++}`;
      out.push(React.createElement(tag, { key }, styled(m[lead ? 2 : 1], leaf, key)));
      rest = rest.slice(m.index + m[0].length);
    }
    return out;
  }

  // Text with [label](url) links (set in the editor by pasting a URL over selected text), bare URLs and inline styles; React nodes only.
  // Links and URLs are lifted out first so style markers inside them are left alone (a link label can still be styled).
  const MD_LINK = /\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)/g;
  function Linkified({ text }) {
    const slots = [];
    const hold = slot => `\uE000${slots.push(slot) - 1}\uE001`;
    let src = String(text).replace(MD_LINK, (m, label, href) => hold({ label, href }));
    src = src.replace(/https?:\/\/[^\s\uE000\uE001]+/g, m => { const tail = (m.match(/[*_~]+$/) || [''])[0]; return hold({ url: m.slice(0, m.length - tail.length) }) + tail; });
    const leaf = (chunk, k) => chunk.split(/\uE000(\d+)\uE001/).map((part, i) => {
      if (i % 2 === 0) return <React.Fragment key={`${k}-${i}`}>{part}</React.Fragment>;
      const slot = slots[Number(part)];
      if (slot.url) return <a key={`${k}-${i}`} href={slot.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--m3-primary)' }}>{slot.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</a>;
      return <a key={`${k}-${i}`} href={slot.href} {...(/^mailto:/i.test(slot.href) ? {} : { target: '_blank', rel: 'noopener noreferrer' })} style={{ color: 'var(--m3-primary)' }}>{styled(slot.label, (c, kk) => [<React.Fragment key={kk}>{c}</React.Fragment>], `${k}-${i}`)}</a>;
    });
    return styled(src, leaf);
  }

  // Shown for images that are not downloaded yet (b.u === null); label says which one is missing.
  function ImagePlaceholder({ label, ratio = '16 / 10', sx = {} }) {
    const { GoogleIcon } = window.Components;
    return (
      <Box
        role="img"
        aria-label={`Missing image: ${label}`}
        sx={{
          aspectRatio: ratio, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
          p: 2, textAlign: 'center', borderRadius: '16px', border: '2px dashed var(--m3-outline-variant)',
          bgcolor: 'var(--m3-surface-container-highest)', color: 'var(--m3-on-surface-variant)', ...sx
        }}
      >
        <GoogleIcon name="image" size={32} />
        <Typography variant="caption">Image placeholder</Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>{label}</Typography>
      </Box>
    );
  }

  // Responsive attributes for a portfolio upload: the server makes 480/800/1200px copies on request (?w=). Other URLs pass through.
  const UPLOAD_IMG = /^\/uploads\/portfolio\/[\w.-]+\.(?!gif$)\w+$/i;
  const responsive = (u, sizes) => (UPLOAD_IMG.test(u || '')
    ? { src: u, srcSet: `${u}?w=480 480w, ${u}?w=800 800w, ${u}?w=1200 1200w, ${u} 1600w`, sizes }
    : { src: u });
  window.Components.responsiveImg = responsive; // also used by the pictures widget (Widgets.js) and photo tiles (SectionShowcase.js)

  function Images({ items }) {
    const cols = items.length === 1 ? '1fr' : items.length === 2 ? 'repeat(auto-fit, minmax(260px, 1fr))' : 'repeat(auto-fit, minmax(220px, 1fr))';
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: 2, alignItems: 'start', my: 2 }}>
        {items.map((b, i) => (
          <Box key={i} component="figure" sx={{ m: 0 }}>
            {!b.u ? <ImagePlaceholder label={b.ph || 'image'} /> : <window.Components.CaseImage b={b} img={responsive(b.u, items.length > 1 ? '(max-width: 900px) 100vw, 400px' : '(max-width: 900px) 100vw, 820px')} />}
            {b.c && <Typography component="figcaption" variant="body2" sx={{ display: 'block', mt: 1, color: 'var(--m3-secondary)', ...(typeSx('body-small') || {}) }}>{b.c}</Typography>}
          </Box>
        ))}
      </Box>
    );
  }

  // Image with a set size: a share of its container's width (page column or grid cell), aligned left / center / right.
  // Phones get a larger share so small images stay legible.
  const IMG_WIDTH = { large: { xs: '100%', md: '75%' }, medium: { xs: '75%', md: '50%' }, small: { xs: '50%', md: '33.333%' } };
  const IMG_SIZES = { large: '(max-width: 900px) 100vw, 620px', medium: '(max-width: 900px) 75vw, 420px', small: '(max-width: 900px) 50vw, 280px' };
  function SizedImage({ b }) {
    const mx = b.align === 'center' ? { ml: 'auto', mr: 'auto' } : b.align === 'right' ? { ml: 'auto', mr: 0 } : { ml: 0, mr: 'auto' };
    return (
      <Box component="figure" sx={{ my: 2, width: IMG_WIDTH[b.size], ...mx }}>
        {!b.u ? <ImagePlaceholder label={b.ph || 'image'} /> : <window.Components.CaseImage b={b} img={responsive(b.u, IMG_SIZES[b.size])} />}
        {b.c && <Typography component="figcaption" variant="body2" sx={{ display: 'block', mt: 1, textAlign: b.align || 'left', color: 'var(--m3-secondary)', ...(typeSx('body-small') || {}) }}>{b.c}</Typography>}
      </Box>
    );
  }

  // Quote: the only element in a case study that sits in a card, highlighted with the current accent.
  // Optional author line: name (by), role and an avatar (u; shown only when an image is set).
  function QuoteCard({ text, style, by, role, avatar }) {
    const { GoogleIcon, Card } = window.Components;
    return (
      <Card variant="filled" sx={{ my: 3, borderRadius: 'var(--m3-shape-large)', backgroundColor: 'var(--m3-primary-container)', color: 'var(--m3-on-primary-container)' }}>
        <Box component="figure" sx={{ display: 'flex', gap: 2, m: 0, p: { xs: 2.5, md: 3 } }}>
          <GoogleIcon name="format_quote" size={32} filled style={{ color: 'inherit', flexShrink: 0, opacity: 0.8 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="blockquote" sx={{ m: 0, fontWeight: 400, fontStyle: 'italic', lineHeight: 1.5, color: 'inherit', ...(typeSx(style) || {}) }}><Linkified text={text} /></Typography>
            {(by || role) && (
              <Box component="figcaption" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                {avatar && <Box component="img" src={UPLOAD_IMG.test(avatar) ? `${avatar}?w=480` : avatar} alt="" loading="lazy" sx={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />}
                <Box sx={{ minWidth: 0 }}>
                  {by && <Typography variant="body1" sx={{ fontWeight: 600, color: 'inherit' }}>{by}</Typography>}
                  {role && <Typography variant="body2" sx={{ color: 'inherit', opacity: 0.8 }}>{role}</Typography>}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Card>
    );
  }

  // List block: one icon before every line; a line is text (**bold** and links work) plus optional pills.
  // b: {icon, items:[{x, tags[]} | string], s}
  // Inline text with styles and links (React text nodes only, nothing is parsed as HTML).
  window.Components = window.Components || {};
  window.Components.Linkified = Linkified; // text with links and **bold** etc., used by ExtraBlocks.js
  const rich = text => <Linkified text={String(text)} />;

  // Table block: b {head, align[], rows[][], c}. The first row is the header when `head` is on. Scrolls sideways on narrow screens.
  function TableBlock({ b }) {
    const rows = b.rows || [];
    const head = b.head !== false && rows.length > 0;
    const cell = (x, n, th) => (
      <Box component={th ? 'th' : 'td'} key={n} sx={{
        textAlign: (b.align || [])[n] || 'left', verticalAlign: 'top', px: 2, py: 1.25, fontWeight: th ? 600 : 400,
        ...(typeSx(th ? 'label-large' : 'body-medium') || {}), color: 'var(--m3-on-surface)'
      }}>{rich(x)}</Box>
    );
    return (
      <Box component="figure" sx={{ m: 0, my: 3 }}>
        <Box sx={{ overflowX: 'auto', borderRadius: 'var(--m3-shape-medium)', backgroundColor: 'var(--m3-surface-container-low)' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            {head && <Box component="thead" sx={{ backgroundColor: 'var(--m3-surface-container-high)' }}><tr>{rows[0].map((x, n) => cell(x, n, true))}</tr></Box>}
            <tbody>
              {rows.slice(head ? 1 : 0).map((r, i) => (
                <Box component="tr" key={i} sx={{ borderTop: (head || i) ? '1px solid var(--m3-outline-variant)' : 'none' }}>{r.map((x, n) => cell(x, n, false))}</Box>
              ))}
            </tbody>
          </Box>
        </Box>
        {b.c && <Typography component="figcaption" variant="body2" sx={{ display: 'block', mt: 1, color: 'var(--m3-secondary)', ...(typeSx('body-small') || {}) }}>{b.c}</Typography>}
      </Box>
    );
  }

  // perLine: each item may carry its own icon (falls back to the list's icon)
  function BulletList({ items, icon, perLine, style }) {
    const { GoogleIcon, LabelPills } = window.Components;
    return (
      <Box component="ul" sx={{ listStyle: 'none', m: 0, mb: 1.5, p: 0 }}>
        {items.map((it, n) => {
          const o = typeof it === 'string' ? { x: it } : it;
          return (
            <Box component="li" key={n} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.75, '&:hover': { '--m3-icon-fill': 1 } }}>
              <GoogleIcon name={(perLine && o.icon) || icon || 'check_circle'} size={20} style={{ color: 'var(--m3-primary)', marginTop: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5, minWidth: 0 }}>
                {o.x && <Typography variant="body1" sx={typeSx(style) || undefined}>{rich(o.x)}</Typography>}
                {o.tags && o.tags.length > 0 && <LabelPills items={o.tags} tone="secondary" size="small" />}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  // Image + text. horizontal: two equal columns (image first on compact screens); vertical: one column, text as wide as the image.
  // side: 'left'|'right' = image first|second (left/right when horizontal, above/below when vertical). b: {u, c, x, side, layout, s}
  function MediaBlock({ b }) {
    const right = b.side === 'right';
    const vertical = b.layout === 'vertical';
    const paras = String(b.x || '').split(/\n{2,}/).filter(Boolean);
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: vertical ? 'minmax(0, 1fr)' : { xs: '1fr', md: '1fr 1fr' }, gap: vertical ? 2 : { xs: 2, md: 4 }, alignItems: 'center', my: 3 }}>
        <Box component="figure" sx={{ m: 0, order: vertical ? (right ? 2 : 0) : { xs: 0, md: right ? 2 : 0 } }}>
          {b.u
            ? <window.Components.CaseImage b={b} img={responsive(b.u, vertical ? '(max-width: 900px) 100vw, 820px' : '(max-width: 900px) 100vw, 480px')} />
            : <ImagePlaceholder label={b.ph || 'image'} />}
          {b.c && <Typography component="figcaption" variant="body2" sx={{ display: 'block', mt: 1, color: 'var(--m3-secondary)', ...(typeSx('body-small') || {}) }}>{b.c}</Typography>}
        </Box>
        <Box sx={{ order: 1, minWidth: 0 }}>
          {paras.map((x, n) => <Typography key={n} variant="body1" sx={{ mb: 1.5, whiteSpace: 'pre-line', ...(typeSx(b.s) || {}) }}><Linkified text={x} /></Typography>)}
        </Box>
      </Box>
    );
  }

  // What a card linked to an article or photo set (b.to with b.fill) shows, read from the live item and cached for a moment
  const linkedCache = new Map();
  const firstPicture = blocks => {
    for (const x of blocks || []) {
      if ((x.t === 'img' || x.t === 'media') && x.u) return x.u;
      if (x.t === 'grid') { const u = firstPicture(x.items); if (u) return u; }
    }
    return null;
  };
  function loadLinked(to) {
    const key = `${to.sec}/${to.slug || (to.video ? `video-${to.video}` : `set-${to.set}`)}`;
    const hit = linkedCache.get(key);
    if (hit && Date.now() - hit.at < 30e3) return hit.promise;
    const api = window.PortfolioApi;
    const promise = to.video
      ? api.videos.list(to.sec).then(r => {
        const v = (r.items || []).find(x => x.id === to.video);
        return v ? { title: v.title, pill: v.category || v.client || 'Video', text: v.description, img: v.youtubeId ? `https://i.ytimg.com/vi/${v.youtubeId}/mqdefault.jpg` : null /* 16:9, no letterbox bars (hqdefault is 4:3 with bars) */ } : null;
      })
      : to.set
      ? Promise.all([api.photos.list(to.sec), api.photoSets.list(to.sec)]).then(([ph, st]) => {
        const set = st.items.find(x => x.id === to.set);
        if (!set) return null;
        const photos = ph.items.filter(x => x.setId === set.id && x.published !== false).sort((a, b2) => b2.id - a.id);
        return { title: set.title, pill: `${photos.length} photo${photos.length === 1 ? '' : 's'}`, text: set.description, img: photos[0] ? (photos[0].thumbUrl || photos[0].url) : null };
      })
      : api.list(to.sec).then(r => {
        const st = r.studies.find(x => x.slug === to.slug);
        return st ? { title: st.title.split(' —')[0], pill: st.kind, text: st.description, img: st.thumb || st.cover || firstPicture(st.blocks), fit: st.thumb ? st.thumbFit : '' } : null;
      });
    linkedCache.set(key, { at: Date.now(), promise });
    promise.catch(() => linkedCache.delete(key));
    return promise;
  }
  function useLinked(to, fill) {
    const [item, setItem] = React.useState(null);
    const on = !!(fill && to && to.sec && (to.slug || to.set || to.video));
    const key = on ? `${to.sec}/${to.slug || to.set || `video-${to.video}`}` : '';
    React.useEffect(() => {
      if (!on) { setItem(null); return undefined; }
      let live = true;
      loadLinked(to).then(d => { if (live) setItem(d); }).catch(() => { if (live) setItem(null); });
      return () => { live = false; };
    }, [key]);
    return item;
  }

  // Card (M3): the page's surface card by default, or elevated / outlined (look). Large (default) = wide 16:10 media and roomy text; small = the compact
  // card of the photo sets (4:3 media, 16dp padding, title-large text), capped at 360px when it sits on the page itself. The image can be switched off (img).
  // b.to makes the whole card a link (a page, section, article, photo set or address); with b.fill it takes its title, label, text and picture from
  // the linked article or photo set, and whatever is typed on the card itself wins over that.
  function CardBlock({ b }) {
    const { Card, SurfaceCard, LabelPills } = window.Components;
    const small = b.size === 'small';
    const Shell = b.look ? Card : SurfaceCard; // no look = the page's own surface card, as before
    const item = useLinked(b.to, b.fill);
    const title = b.h || (item && item.title) || '';
    const pill = b.pill || (item && item.pill) || '';
    const text = b.x || (item && item.text) || '';
    const pic = b.u || (item && item.img) || null;
    const whole = b.fit ? b.fit === 'contain' : !b.u && item && item.fit === 'contain'; // the card's own choice wins, else the linked page's thumbnail setting
    const paras = String(text).split(/\n{2,}/).filter(Boolean);
    const linked = !!(b.to && (b.to.page || b.to.href));
    return (
      <Shell {...(b.look ? { variant: b.look } : {})} {...(linked ? { clickable: true, onClick: () => window.SiteLinks.go(b.to), ...(title ? {} : { 'aria-label': 'Open' }) } : {})} sx={{ my: 3, overflow: 'hidden', ...(small && { maxWidth: 360 }) }}>
        {b.img !== false && (pic
          ? <Box component="img" {...responsive(pic, small ? '(max-width: 900px) 100vw, 360px' : '(max-width: 900px) 100vw, 820px')} alt={b.c || ''} loading="lazy" sx={{ width: '100%', aspectRatio: small ? '4 / 3' : '16 / 10', objectFit: whole ? 'contain' : 'cover', display: 'block', backgroundColor: 'var(--m3-surface-container-highest)' }} />
          : <ImagePlaceholder label={b.ph || 'image'} ratio={small ? '4 / 3' : '16 / 10'} sx={{ borderRadius: 0, border: 0 }} />)}
        {(title || pill || paras.length > 0) && (
          <Box sx={{ p: small ? 2 : 3, '& > :last-child': { mb: 0 } }}>
            {pill && <LabelPills items={[pill]} sx={{ mb: 1 }} />}
            {title && <Typography variant="h6" component="h3" sx={{ m: 0, mb: paras.length ? (small ? 0.5 : 1) : 0, ...(small ? window.Components.m3Typescale.titleMedium : window.Components.m3Typescale.titleLarge) }}>{title}</Typography>}
            {paras.map((x, n) => <Typography key={n} variant={small ? 'body2' : 'body1'} sx={{ mb: 1.5, whiteSpace: 'pre-line', color: 'var(--m3-on-surface-variant)', ...(typeSx(b.s) || {}) }}><Linkified text={x.trim()} /></Typography>)}
          </Box>
        )}
      </Shell>
    );
  }

  // Highlight box (stored as 'highlight', older pages say 'hero'): title, subtitle and pills on an accent (primary) surface. b: {x, sub, tags[]}
  function HighlightBlock({ b }) {
    const { Card } = window.Components;
    return (
      <Card variant="filled" sx={{ my: 3, borderRadius: 'var(--m3-shape-extra-large)', backgroundColor: 'var(--m3-primary)', color: 'var(--m3-on-primary)' }}>
        <Box sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start' }}>
          {b.x && <Typography variant="h4" component="h2" sx={{ m: 0, color: 'inherit', ...window.Components.m3Typescale.headlineMedium }}>{b.x}</Typography>}
          {b.sub && <Typography variant="body1" sx={{ maxWidth: 680, color: 'inherit', opacity: 0.9 }}>{b.sub}</Typography>}
          {(b.tags || []).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
              {b.tags.map((tag, n) => (
                // Static pill on the accent surface: translucent on-primary fill, M3 chip metrics (32dp, small shape, label-large)
                <Box key={n} component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 32, px: 1.5, borderRadius: 'var(--m3-shape-small)', backgroundColor: 'color-mix(in srgb, var(--m3-on-primary) 16%, transparent)', color: 'var(--m3-on-primary)', ...window.Components.m3Typescale.labelLarge }}>{tag}</Box>
              ))}
            </Box>
          )}
        </Box>
      </Card>
    );
  }

  const GRID_GAP = { small: 1.5, medium: 3, large: 5 };

  // Grid of blocks: `cols` columns from the md breakpoint (2 on tablets when 3-4 cols, 1 on phones). Items may span columns.
  // Vertical layout: one column, items stacked top to bottom (columns, spans and alignment do not apply).
  // Grid surface (b.surface): a tonal or coloured container with padding (b.pad) around the whole grid
  const GRID_SURFACE = {
    low: ['var(--m3-surface-container-low)', 'var(--m3-on-surface)'],
    tonal: ['var(--m3-surface-container-high)', 'var(--m3-on-surface)'],
    primary: ['var(--m3-primary-container)', 'var(--m3-on-primary-container)'],
    secondary: ['var(--m3-secondary-container)', 'var(--m3-on-secondary-container)'],
    tertiary: ['var(--m3-tertiary-container)', 'var(--m3-on-tertiary-container)']
  };
  const GRID_PAD = { none: 0, small: { xs: 1.5, md: 2 }, medium: { xs: 2, md: 3 }, large: { xs: 3, md: 5 }, xlarge: { xs: 4, md: 8 } };
  // Custom background (b.surface 'custom', b.bg hex): ink is near-black or white, whichever reads better (WCAG luminance), and the
  // theme roles the blocks inside read (text, primary accents, captions, dividers) are pointed at it, so the content follows.
  const customSurface = hex => {
    const lin = c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
    const [r, g, bl] = [1, 3, 5].map(i => lin(parseInt(hex.slice(i, i + 2), 16) / 255));
    const ink = 0.2126 * r + 0.7152 * g + 0.0722 * bl > 0.179 ? '#1b1b1f' : '#ffffff';
    const soft = pct => `color-mix(in srgb, ${ink} ${pct}%, transparent)`;
    return {
      backgroundColor: hex, color: ink,
      '--m3-on-surface': ink, '--m3-on-surface-variant': soft(80), '--m3-primary': ink, '--m3-secondary': soft(80),
      '--m3-outline': soft(50), '--m3-outline-variant': soft(22)
    };
  };
  window.Components.Surfaces = { GRID_SURFACE, customSurface }; // grid backgrounds, reused by the widget block (Widgets.js)
  function GridBlock({ b, compact }) {
    const surface = b.surface === 'custom' ? (b.bg ? customSurface(b.bg) : null) : GRID_SURFACE[b.surface] && { backgroundColor: GRID_SURFACE[b.surface][0], color: GRID_SURFACE[b.surface][1] };
    // The optional title is a section heading above the items; empty = nothing is rendered
    const title = String(b.title || '').trim();
    const heading = title && <Typography variant="h6" component="h2" sx={{ mb: 2, ...(typeSx(headingStyle(2, undefined, compact)) || {}) }}>{title}</Typography>;
    const body = <GridBody b={b} bare={!!surface} />;
    if (!surface) return heading ? <Box sx={{ my: 3 }}>{heading}{React.cloneElement(body, { flush: true })}</Box> : body;
    return <Box className="blk-surface" sx={{ my: 3, p: GRID_PAD[b.pad] ?? GRID_PAD.medium, borderRadius: 'var(--m3-shape-extra-large)', ...surface }}>{heading}{body}</Box>;
  }

  function GridBody({ b, bare, flush }) {
    const cols = Math.min(6, Math.max(2, b.cols || 2));
    const gap = GRID_GAP[b.gap] || 3;
    const my = bare || flush ? 0 : 3;
    const compact = cols >= 3 && b.layout !== 'vertical'; // headings in narrow cells step down one style
    const inner = { minWidth: 0, '& > :first-child': { mt: 0 }, '& > :last-child': { mb: 0 } };
    if (b.layout === 'vertical') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap, my }}>
          {(b.items || []).map((item, n) => <Box key={n} sx={inner}><Blocks blocks={[item]} compact={compact} /></Box>)}
        </Box>
      );
    }
    const two = 'repeat(2, minmax(0, 1fr))';
    const colsAt = { md: Math.min(cols, 4), lg: cols };
    if (b.masonry) {
      // Items flow down CSS columns (each keeps its own height) instead of sitting in rows
      return (
        <Box sx={{ columnCount: { xs: b.keep ? 2 : 1, sm: b.keep || cols > 2 ? 2 : 1, md: colsAt.md, lg: colsAt.lg }, columnGap: `${gap * 8}px`, my }}>
          {(b.items || []).map((item, n) => <Box key={n} sx={{ ...inner, breakInside: 'avoid', mb: `${gap * 8}px` }}><Blocks blocks={[item]} compact={compact} /></Box>)}
        </Box>
      );
    }
    const track = b.ratio && b.ratio.split(':').length === cols ? b.ratio.split(':').map(n => `minmax(0, ${n}fr)`).join(' ') : null;
    const rep = n => `repeat(${n}, minmax(0, 1fr))`;
    const rows = (b.items || []).some(it => it.rowSpan > 1);
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: b.keep ? two : '1fr', sm: b.keep || cols > 2 ? two : '1fr', md: track || rep(colsAt.md), lg: track || rep(colsAt.lg) }, ...(rows ? { gridAutoFlow: 'row dense' } : {}), gap, alignItems: b.align === 'center' ? 'center' : b.align === 'stretch' ? 'stretch' : 'start', my }}>
        {(b.items || []).map((item, n) => (
          <Box key={n} sx={{ ...inner, gridColumn: { md: `span ${Math.min(item.span || 1, colsAt.md)}`, lg: `span ${Math.min(item.span || 1, cols)}` }, ...(item.rowSpan > 1 ? { gridRow: { md: `span ${item.rowSpan}` } } : {}), ...(item.valign ? { alignSelf: item.valign } : {}), ...(b.align === 'stretch' || item.rowSpan > 1 ? { display: 'flex', flexDirection: 'column', '& > *': { flexGrow: 1 } } : {}) }}>
            <Blocks blocks={[item]} compact={compact} />
          </Box>
        ))}
      </Box>
    );
  }

  // Block width (b.width): narrow = reading width, wide = past the page column, full = edge to edge. The page column is centred,
  // so a symmetric negative margin works at any viewport; body clips the sideways overflow of 100vw.
  const WIDTH_SX = {
    narrow: { maxWidth: 760, mx: 'auto' },
    wide: { width: 'min(1480px, calc(100vw - 32px))', maxWidth: 'none', ml: 'calc(50% - min(740px, 50vw - 16px))' },
    full: { width: '100vw', maxWidth: 'none', ml: 'calc(50% - 50vw)' }
  };
  // Anchors for the "On this page" block (toc): each top-level heading block gets an id from its text, shared through this context
  const HeadingIds = React.createContext(null);
  const slug = t => String(t || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'part';
  function headingIds(blocks) {
    const ids = new Map(), used = new Set();
    blocks.forEach(b => {
      if (b.t !== 'h' || b.l < 2 || !String(b.x || '').trim()) return;
      let id = 'sec-' + slug(b.x), n = 2;
      while (used.has(id)) id = `sec-${slug(b.x)}-${n++}`;
      used.add(id); ids.set(b, id);
    });
    return ids;
  }
  // The toc block's links: the page's headings (sections, and sub-sections with depth 3)
  function tocItems(blocks, ids, depth) {
    const levels = blocks.filter(b => ids.has(b)).map(b => b.l);
    const top = levels.length ? Math.min(...levels) : 2;
    return blocks.filter(b => ids.has(b) && (b.l === top || (depth === 3 && b.l === top + 1))).map(b => ({ id: ids.get(b), label: b.x, level: b.l === top ? 2 : 3 }));
  }
  function WithToc({ blocks, ids, children }) {
    const { OnThisPage, TocLayout } = window.Components;
    const toc = blocks.find(b => b.t === 'toc');
    if (!toc || !OnThisPage) return children;
    const items = tocItems(blocks, ids, toc.depth === 3 ? 3 : 2);
    return <TocLayout side={toc.side === 'left' ? 'left' : 'right'} toc={<OnThisPage title={toc.h || undefined} items={items} />}>{children}</TocLayout>;
  }

  function BlockWidth({ width, children }) {
    return <Box className="blk-width" sx={{ minWidth: 0, ...WIDTH_SX[width] }}>{children}</Box>;
  }

  // Page spacing (study.spacing): one even gap between blocks instead of each block's own margins. Headings stay close to what
  // follows them, and an item's inner trailing margin is dropped so the gap is exact.
  const BLOCK_GAP = { small: 32, medium: 64, large: 96 };
  function Spaced({ spacing, children }) {
    const gap = BLOCK_GAP[spacing];
    if (!gap) return <>{children}</>;
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: `${gap}px`,
        '&& > *': { mt: 0, mb: 0 }, '&& > * > :last-child': { mb: 0 },
        '&& > .blk-h': { mb: `${Math.min(gap, 8) - gap}px` }
      }}>{children}</Box>
    );
  }

  function Blocks({ blocks, spacing, compact }) {
    const { GoogleIcon, Button, Card } = window.Components;
    const ids = React.useContext(HeadingIds);
    const out = [];
    let i = 0;
    while (i < blocks.length) {
      const b = blocks[i];
      const from = out.length;
      // Consecutive full-width images share one row; sized ones stand alone.
      if (b.t === 'img' && !b.size) {
        const group = [];
        while (i < blocks.length && blocks[i].t === 'img' && !blocks[i].size) group.push(blocks[i++]);
        out.push(<Images key={`img${i}`} items={group} />);
        continue;
      }
      if (b.t === 'img') {
        out.push(<SizedImage key={i} b={b} />);
      } else if (b.t === 'quote') {
        out.push(<QuoteCard key={i} text={b.x} style={b.s} by={b.by} role={b.role} avatar={b.u} />);
      } else if (b.t === 'list') {
        out.push(<BulletList key={i} items={b.items || []} icon={b.icon} perLine={b.perLine} style={b.s} />);
      } else if (b.t === 'table') {
        out.push(<TableBlock key={i} b={b} />);
      } else if (b.t === 'divider') {
        out.push(<Divider key={i} look={b.look} size={b.size} />);
      } else if (b.t === 'highlight' || b.t === 'hero') {
        out.push(<HighlightBlock key={i} b={b} />);
      } else if (b.t === 'showcase') {
        out.push(<window.Components.SectionShowcase key={i} b={b} />);
      } else if (b.t === 'media') {
        out.push(<MediaBlock key={i} b={b} />);
      } else if (b.t === 'grid') {
        out.push(<GridBlock key={i} b={b} compact={compact} />);
      } else if (b.t === 'card') {
        out.push(<CardBlock key={i} b={b} />);
      } else if (b.t === 'carousel') {
        out.push(<window.Components.ImageCarousel key={i} items={b.items} ratio={b.ratio} orient={b.orient} fit={b.fit} auto={b.auto} />);
      } else if (b.t === 'hp') {
        out.push(
          <Box key={i} sx={{ mt: 3 }}>
            {b.h && <Typography variant="h6" component="h2" sx={{ mb: 1, ...(typeSx(headingStyle(2, b.hs, compact)) || {}) }}>{b.h}</Typography>}
            {String(b.x || '').split(/\n{2,}/).filter(Boolean).map((x, n) => <Typography key={n} variant="body1" sx={{ mb: 1.5, maxWidth: 820, whiteSpace: 'pre-line', ...(typeSx(b.s) || {}) }}><Linkified text={x.trim()} /></Typography>)}
          </Box>
        );
      } else if (b.t === 'h') {
        out.push(<Typography key={i} id={ids ? ids.get(b) : undefined} className="blk-h" variant="h6" component={b.l === 3 ? 'h3' : 'h2'} sx={{ mt: 3, mb: 1, scrollMarginTop: 96, ...(typeSx(headingStyle(b.l === 3 ? 3 : 2, b.s, compact)) || {}) }}>{b.x}</Typography>);
      } else if (b.t === 'toc') {
        // drawn beside the content by WithToc (CaseStudyPage), not in the flow
      } else if (b.t === 'code') {
        out.push(<CodeBlock key={i} b={b} />);
      } else if (b.t === 'video') {
        out.push(<window.Components.VideoBlock key={i} u={b.u} c={b.c} download={b.download === true} />);
      } else if (b.t === 'file') {
        out.push(<window.Components.FileBlock key={i} b={b} />);
      } else if (window.Components.ExtraBlocks[b.t]) {
        const Extra = window.Components.ExtraBlocks[b.t];
        out.push(<Extra key={i} b={b} />);
      } else if (b.t === 'figma') {
        out.push(
          <Box key={i} sx={{ my: 2 }}>
            <window.Components.ConsentGate service="Figma" href={b.link || undefined} minHeight={320}>
            <Box
              component="iframe"
              src={b.u}
              title="Figma prototype"
              loading="lazy"
              allowFullScreen
              sx={{ width: '100%', height: { xs: 480, md: 640 }, border: '1px solid var(--m3-outline-variant)', borderRadius: '16px', bgcolor: 'var(--m3-surface-container-highest)' }}
            />
            </window.Components.ConsentGate>
            {b.link && (
              <Button variant="outlined" size="small" href={b.link} target="_blank" rel="noopener noreferrer" icon={<GoogleIcon name="open_in_new" size={16} />} sx={{ mt: 1.5 }}>
                Open prototype in new window
              </Button>
            )}
          </Box>
        );
      } else if (b.t === 'p') {
        // Format (b.as) is chosen per paragraph in the editor; without one it is a plain paragraph.
        if (b.as === 'credit') {
          const [name, ...role] = String(b.x).split(' - ');
          out.push(
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
              <GoogleIcon name="person" size={20} style={{ color: 'var(--m3-on-surface-variant)' }} />
              <Typography variant="body1"><strong>{name}</strong>{role.length > 0 && <Box component="span" sx={{ color: 'text.secondary' }}> · {role.join(' - ')}</Box>}</Typography>
            </Box>
          );
        } else if (b.as === 'check') {
          out.push(
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.75 }}>
              <GoogleIcon name="check_circle" size={20} style={{ color: 'var(--m3-primary)', marginTop: 2 }} />
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line', ...(typeSx(b.s) || {}) }}><Linkified text={b.x} /></Typography>
            </Box>
          );
        } else if (b.as === 'quote') {
          // Quote: the only element in a case study that sits in a card, highlighted with the current accent.
          out.push(
            <Card key={i} variant="filled" sx={{ my: 3, borderRadius: 'var(--m3-shape-large)', backgroundColor: 'var(--m3-primary-container)', color: 'var(--m3-on-primary-container)' }}>
              <Box sx={{ display: 'flex', gap: 2, p: { xs: 2.5, md: 3 } }}>
                <GoogleIcon name="format_quote" size={32} filled style={{ color: 'inherit', flexShrink: 0, opacity: 0.8 }} />
                <Typography variant="h6" component="blockquote" sx={{ m: 0, fontWeight: 400, fontStyle: 'italic', lineHeight: 1.5, color: 'inherit' }}><Linkified text={b.x} /></Typography>
              </Box>
            </Card>
          );
        } else if (b.as === 'note') {
          out.push(
            <Box key={i} sx={{ my: 1.5, pl: 2, maxWidth: 820, borderLeft: '4px solid var(--m3-outline-variant)' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}><Linkified text={b.x} /></Typography>
            </Box>
          );
        } else {
          const paras = String(b.x).split(/\n{2,}/).filter(Boolean).map((x, n) => <Typography key={`${i}-${n}`} variant="body1" sx={{ mb: 1.5, maxWidth: 820, whiteSpace: 'pre-line', ...(typeSx(b.s) || {}) }}><Linkified text={x.trim()} /></Typography>);
          // With page spacing, one paragraph block is one item (its paragraphs keep their own spacing).
          out.push(...(BLOCK_GAP[spacing] ? [<Box key={i}>{paras}</Box>] : paras));
        }
      }
      if (b.width && WIDTH_SX[b.width]) for (let k = from; k < out.length; k++) out[k] = <BlockWidth key={out[k].key} width={b.width}>{out[k]}</BlockWidth>;
      i++;
    }
    return <Spaced spacing={spacing}>{out}</Spaced>;
  }

  function splitSections(blocks) {
    const levels = blocks.filter(b => b.t === 'h' && b.l >= 2).map(b => b.l);
    const top = levels.length ? Math.min(...levels) : 2;
    const intro = [];
    const sections = [];
    blocks.forEach(b => {
      if (b.t === 'h' && b.l <= 1) return; // page title is rendered by the header
      if (b.t === 'h' && b.l === top) { sections.push({ title: b.x, style: b.s, block: b, blocks: [] }); return; }
      (sections.length ? sections[sections.length - 1].blocks : intro).push(b);
    });
    return { intro, sections };
  }

  // Code block (coding blog): language label, copy button and a monospace, horizontally scrollable body.
  function CodeBlock({ b }) {
    const { IconButton, Tooltip, GoogleIcon } = window.Components;
    const [copied, setCopied] = React.useState(false);
    const copy = async () => {
      try { await navigator.clipboard.writeText(b.code || ''); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { /* clipboard unavailable */ }
    };
    return (
      <Box sx={{ my: 2, borderRadius: 'var(--m3-shape-large)', overflow: 'hidden', backgroundColor: 'var(--m3-surface-container-highest)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 2, pr: 0.5, py: 0.25, backgroundColor: 'var(--m3-surface-container-high)' }}>
          <Typography variant="labelMedium" sx={{ color: 'text.secondary' }}>{b.c || b.lang || 'Code'}</Typography>
          <Tooltip title={copied ? 'Copied' : 'Copy code'}>
            <IconButton size="small" aria-label="Copy code" onClick={copy}><GoogleIcon name={copied ? 'check' : 'content_copy'} size={18} /></IconButton>
          </Tooltip>
        </Box>
        <Box component="pre" tabIndex={0} sx={{ m: 0, p: 2, overflowX: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--m3-on-surface)', tabSize: 2 }}>
          <code>{b.code}</code>
        </Box>
      </Box>
    );
  }

  const fmtDate = d => (d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '');

  // label: header text before the pills ('UX case study' by default; blog posts show their date instead)
  // bare: only the blocks, without header, lead picture and tags (the Home landing page, whose hero sits above it)
  function CaseStudyPage({ study, onBack, label = 'UX case study', backLabel = 'Case studies', showDate = false, bare = false }) {
    const { PortfolioPageHeader, Chip } = window.Components;
    const { intro, sections } = React.useMemo(() => splitSections(study.blocks), [study]);
    const ids = React.useMemo(() => headingIds(study.blocks), [study]);
    // With an "On this page" block, everything below the page hero shares the row with the menu (WithToc)
    if (bare) return <HeadingIds.Provider value={ids}><Box component="article"><WithToc blocks={study.blocks} ids={ids}><Blocks blocks={study.blocks} spacing={study.spacing} /></WithToc></Box></HeadingIds.Provider>;
    const hero = intro.find(b => b.t === 'img');
    const lead = intro.filter(b => b !== hero);

    return (
      <Box component="article">
        <PortfolioPageHeader
          overline={study.showPill === false ? undefined : showDate ? [fmtDate(study.date), study.kind].filter(Boolean).join(' · ') : [study.label || label, study.kind].filter(Boolean).join(' · ')}
          title={study.title}
          subtitle={study.description}
          titleSx={typeSx(study.titleStyle || 'title-large')}
          subtitleSx={typeSx(study.subtitleStyle)}
          backLabel={backLabel}
          onBack={onBack}
        />

        {hero && (hero.u ? (
          <Box
            component="img"
            {...responsive(hero.u, '(max-width: 1200px) 100vw, 1200px')}
            fetchpriority="high"
            alt=""
            sx={{ width: '100%', maxHeight: 420, objectFit: 'cover', borderRadius: '28px', display: 'block', mb: 3 }}
          />
        ) : (
          <Box sx={{ mb: 3 }}><ImagePlaceholder label={hero.ph || 'hero image'} ratio="21 / 9" sx={{ borderRadius: '28px' }} /></Box>
        ))}
        <HeadingIds.Provider value={ids}>
          <WithToc blocks={study.blocks} ids={ids}>
            {lead.length > 0 && (
              <Box sx={{ mb: 5 }}><Blocks blocks={lead} spacing={study.spacing} /></Box>
            )}

            {sections.map((s, idx) => (
              <Box component="section" key={idx} sx={{ mb: 5 }}>
                <Typography id={ids.get(s.block)} variant="h5" component="h2" sx={{ mb: 2, scrollMarginTop: 96, ...(typeSx(headingStyle(2, s.style)) || {}) }}>{s.title}</Typography>
                <Blocks blocks={s.blocks} spacing={study.spacing} />
              </Box>
            ))}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(study.tags || []).map(t => <Chip key={t} size="small" variant="outlined" label={t} />)}
            </Box>
          </WithToc>
        </HeadingIds.Provider>
      </Box>
    );
  }

  window.Components = window.Components || {};
  window.Components.CaseStudyPage = CaseStudyPage;
  window.LinkedItems = { useLinked, load: loadLinked }; // the linked article / photo set of a card, also used by the link widget (Widgets.js)
  window.Components.ImagePlaceholder = ImagePlaceholder;
})();
